import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../../../auth/AuthContext'
import type {
    AdjuntoDTO,
    CRMTextoDTO,
    CRMTextoTipo,
    PolizaAseguradoDTO,
    PolizaDTO,
    ReciboDTO,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { GENEROS, PARENTESCOS } from '../../../utils/catalogs'
import { formatWithAge, parseDate } from '../../../utils/dates'
import * as Permisos from '../../../utils/Permisos'
import {
    aseguradoraName,
    canRenewPolicy,
    CONDUCTO_LABELS,
    createPolicyNote,
    currencyCode,
    daysUntil,
    deletePolicyAdjunto,
    errorMessage,
    fetchPolicyAndReciboAdjuntos,
    fetchPolicyBitacora,
    fileExt,
    fileSizeLabel,
    findCaratula,
    FORMA_PAGO,
    formatActivityDate,
    formatSpokenDate,
    isBusy,
    isPaidRecibo,
    isPendingRecibo,
    labelize,
    MAX_UPLOAD_BYTES,
    money,
    NEW_POLICY_PATH,
    nextPendingRecibo,
    NOTE_TYPES,
    noteMeta,
    personName,
    POLICIES_PATH,
    policyCommission,
    policyPath,
    ramoName,
    reciboIdsOf,
    reciboTag,
    reciboChip,
    renewBlockedMessage,
    restanText,
    statusKey,
    statusView,
    sumTotal,
    uploadPolicyFiles,
    vehicleLabel,
    vigenciaView,
} from '../common/policy-common'
import type { BitacoraFilter, LoadState, PolicyFormLocationState, PolicyReadLocationState, StatusChip } from '../common/policy-common'

export type PolicyReadTab = 'general' | 'asegurados' | 'primas' | 'recibos' | 'bitacora' | 'adjuntos'
export type AdjuntoFilter = 'todos' | 'poliza' | 'recibos' | 'pdf' | 'img' | 'xls'

export interface AdjuntoRow {
    adjunto: AdjuntoDTO
    tag: string
    fromRecibo: boolean
}
export type ActivityTone = BitacoraFilter | 'pago' | 'emision'

export interface PolicyReadTabItem {
    id: PolicyReadTab
    label: string
    count?: number | null
}

export interface PolicyFact {
    label: string
    value?: string | null
    wide?: boolean
    to?: string
}

export interface AseguradoCard {
    key: string
    rol: string
    nombre: string
    esContratante: boolean
    vehiculo: boolean
    facts: PolicyFact[]
}

export interface ReciboRow {
    key: string
    numero: string
    vence: string
    prima: string
    derecho: string
    recargo: string
    iva: string
    total: string
    chip: Omit<StatusChip, 'tone'>
    pagable: boolean
    recibo: ReciboDTO
}

export interface DesgloseRow {
    label: string
    value: string
    total?: boolean
}

export interface PendingUpload {
    key: string
    name: string
    ext: string
    size: string
    tooBig: boolean
}

export interface ActivityItem {
    key: string
    title: string
    date: string
    tone: ActivityTone
}

/** Forma que espera `collections/PaymentDrawer`: el recibo con su póliza y días al vencimiento. */
export type PayableRecibo = ReciboDTO & { poliza: PolizaDTO; days: number | null }

const DETAIL_ERROR = 'No se pudo cargar la póliza.'
const BITACORA_ERROR = 'No se pudo cargar la bitácora.'
const ADJUNTOS_ERROR = 'No se pudieron cargar los adjuntos.'
const IMAGE_EXT = ['JPG', 'JPE', 'PNG', 'WEB', 'GIF']
const ACTIVITY_LIMIT = 4
const SNIPPET_LENGTH = 90

const plainText = (html?: string | null) => String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const snippet = (text: string) => (text.length > SNIPPET_LENGTH ? `${text.slice(0, SNIPPET_LENGTH).trimEnd()}…` : text)

const timeOf = (value?: string | null) => parseDate(value)?.toMillis() ?? 0

const formaLabel = (forma?: string | null) => FORMA_PAGO.find((item) => item.key === forma)?.label || labelize(forma)

const aseguradoRol = (asegurado: PolizaAseguradoDTO, index: number) => {
    const parentesco = PARENTESCOS.find((item) => item.id === asegurado.parentesco)?.label
    if (parentesco) return parentesco
    return index === 0 ? 'Titular' : `Asegurado ${index + 1}`
}

export const usePolicyReadModel = () => {
    const { polizaId: routeId } = useParams<{ polizaId: string }>()
    const polizaId = Number(routeId) || null
    const history = useHistory()
    const { canAccess } = useAuth()
    const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const [poliza, setPoliza] = useState<PolizaDTO | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [tab, setTab] = useState<PolicyReadTab>('general')
    const [toast, setToast] = useState<string | null>(null)
    const [closing, setClosing] = useState(false)
    const [paying, setPaying] = useState<PayableRecibo | null>(null)
    const [bitacora, setBitacora] = useState<CRMTextoDTO[]>([])
    const [bitacoraState, setBitacoraState] = useState<LoadState>('idle')
    const [bitacoraError, setBitacoraError] = useState<string | null>(null)
    const [noteType, setNoteType] = useState<CRMTextoTipo>(NOTE_TYPES[0].id)
    const [note, setNote] = useState('')
    const [savingNote, setSavingNote] = useState(false)
    const [bitacoraFilter, setBitacoraFilter] = useState<BitacoraFilter>('todo')
    const [adjuntos, setAdjuntos] = useState<AdjuntoDTO[]>([])
    const [adjuntosState, setAdjuntosState] = useState<LoadState>('idle')
    const [adjuntosError, setAdjuntosError] = useState<string | null>(null)
    const [adjuntoFilter, setAdjuntoFilter] = useState<AdjuntoFilter>('todos')
    const [uploading, setUploading] = useState(false)
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

    const canBitacora = canAccess(Permisos.BITACORA_R)
    const canAdjuntos = canAccess(Permisos.ADJUNTOS_R)

    const showToast = (message: string) => {
        clearTimeout(toastTimer.current)
        setToast(message)
        toastTimer.current = setTimeout(() => setToast(null), 3500)
    }

    useEffect(() => () => clearTimeout(toastTimer.current), [])

    const savedMessage = (history.location.state as PolicyReadLocationState | null)?.savedMessage
    useEffect(() => {
        if (!savedMessage) return
        showToast(savedMessage)
        history.replace(history.location.pathname)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedMessage])

    useEffect(() => {
        setPoliza(null)
        setTab('general')
        setNote('')
        setBitacoraFilter('todo')
        setAdjuntoFilter('todos')
        setConfirmDeleteId(null)
        setPendingFiles([])
        setAdjuntos([])
        setAdjuntosState('idle')
        setClosing(false)
        setPaying(null)
    }, [polizaId])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                if (!polizaId) throw new Error('La póliza solicitada no existe.')
                const response = await APIInvoke.invokeGET(`/polizas/${polizaId}`)
                const payload: WrapperResponse<PolizaDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || DETAIL_ERROR)
                setPoliza(payload.body)
            } catch (loadError) {
                if (active) {
                    setPoliza(null)
                    setError(errorMessage(loadError, DETAIL_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [polizaId, reloadToken])

    useEffect(() => {
        if (!polizaId || !canBitacora) return undefined
        let active = true
        const load = async () => {
            try {
                setBitacoraState('loading')
                setBitacoraError(null)
                const textos = await fetchPolicyBitacora(polizaId)
                if (!active) return
                setBitacora(textos)
                setBitacoraState('ready')
            } catch (loadError) {
                if (active) {
                    setBitacoraState('error')
                    setBitacoraError(errorMessage(loadError, BITACORA_ERROR))
                }
            }
        }
        load()
        return () => { active = false }
    }, [polizaId, canBitacora])

    useEffect(() => {
        if (!poliza || !canAdjuntos) return undefined
        let active = true
        const reciboIds = reciboIdsOf(poliza)
        const load = async () => {
            try {
                setAdjuntosState((current) => (current === 'ready' ? current : 'loading'))
                setAdjuntosError(null)
                const items = await fetchPolicyAndReciboAdjuntos(poliza.id, reciboIds)
                if (!active) return
                setAdjuntos(items)
                setAdjuntosState('ready')
            } catch (loadError) {
                if (active) {
                    setAdjuntosState('error')
                    setAdjuntosError(errorMessage(loadError, ADJUNTOS_ERROR))
                }
            }
        }
        load()
        return () => { active = false }
    }, [poliza, canAdjuntos])

    const reload = () => setReloadToken((token) => token + 1)

    const openFile = async (path?: string | null) => {
        try {
            const url: string | null | undefined = await APIInvoke.resolveStoredFileUrl(path)
            if (url) window.open(url, '_blank', 'noopener')
        } catch (fileError) {
            showToast(errorMessage(fileError, 'No se pudo abrir el archivo.'))
        }
    }

    const caratula = adjuntosState === 'ready' ? findCaratula(adjuntos.filter((item) => item.reciboId == null)) : undefined
    const caratulaPath = caratula?.path || null

    const openCaratula = () => {
        if (caratulaPath) openFile(caratulaPath)
    }

    const caratulaHint = (() => {
        if (caratulaPath) return 'Abrir carátula'
        if (!canAdjuntos) return 'Sin permiso para ver adjuntos'
        if (isBusy(adjuntosState)) return 'Buscando carátula…'
        if (adjuntosState === 'error') return 'No se pudieron cargar los adjuntos'
        return 'Esta póliza no tiene carátula adjunta'
    })()

    const canRenew = canRenewPolicy(poliza)

    const renewPolicy = () => {
        if (!poliza) return
        if (!canRenew) {
            showToast(renewBlockedMessage(poliza))
            return
        }
        const state: PolicyFormLocationState = { polizaId: poliza.id, action: 'renovar', backUrl: policyPath(poliza.id) }
        history.push(NEW_POLICY_PATH, state)
    }

    const editPolicy = () => {
        if (!poliza) return
        const state: PolicyFormLocationState = { backUrl: policyPath(poliza.id) }
        history.push(`${policyPath(poliza.id)}/edit`, state)
    }

    const addNote = async () => {
        const texto = note.trim()
        if (!texto || !poliza) return
        try {
            setSavingNote(true)
            const created = await createPolicyNote(poliza, noteType, texto)
            if (created) setBitacora((items) => [created, ...items])
            setNote('')
            setBitacoraFilter('todo')
            showToast('Registro agregado a la bitácora')
        } catch (saveError) {
            showToast(errorMessage(saveError, 'No se pudo guardar la bitácora.'))
        } finally {
            setSavingNote(false)
        }
    }

    const selectFiles = (fileList: FileList | null) => {
        const files = Array.from(fileList || [])
        if (files.length) setPendingFiles(files)
    }

    const cancelUpload = () => {
        if (!uploading) setPendingFiles([])
    }

    const confirmUpload = async () => {
        const files = pendingFiles
        if (!files.length || !poliza || files.some((file) => file.size > MAX_UPLOAD_BYTES)) return
        try {
            setUploading(true)
            const created = await uploadPolicyFiles(poliza.id, files)
            setAdjuntos((items) => [...created, ...items])
            setAdjuntoFilter('todos')
            setPendingFiles([])
            showToast(files.length === 1 ? 'Archivo subido' : `${files.length} archivos subidos`)
        } catch (uploadError) {
            showToast(errorMessage(uploadError, 'No se pudo subir el archivo.'))
        } finally {
            setUploading(false)
        }
    }

    const pendingUploads: PendingUpload[] = pendingFiles.map((file, index) => ({
        key: `${file.name}-${index}`,
        name: file.name,
        ext: fileExt(file.name),
        size: fileSizeLabel(file.size),
        tooBig: file.size > MAX_UPLOAD_BYTES,
    }))

    const deleteAdjunto = async (id: number) => {
        try {
            await deletePolicyAdjunto(id)
            setAdjuntos((items) => items.filter((item) => item.id !== id))
            setConfirmDeleteId(null)
            showToast('Archivo eliminado')
        } catch (deleteError) {
            showToast(errorMessage(deleteError, 'No se pudo eliminar el archivo.'))
        }
    }

    const payRecibo = (recibo: ReciboDTO) => {
        if (!poliza) return
        setPaying({ ...recibo, poliza, days: daysUntil(recibo.fecVence) })
    }

    const paymentCompleted = (_id: number, message: string) => {
        setPaying(null)
        showToast(message)
        reload()
    }

    const closeCompleted = (closed?: PolizaDTO | null) => {
        setClosing(false)
        showToast(`Póliza ${closed?.noPoliza || poliza?.noPoliza || ''} cerrada`)
        reload()
    }

    const currency = currencyCode(poliza?.moneda)
    const cliente = poliza?.cliente
    const name = cliente ? personName(cliente) : ''
    const status = poliza ? statusView(poliza.status, poliza.fechaVigenciaDe, poliza.fechaVigenciaA) : null
    const closed = status?.tone === 'closed'
    const forma = formaLabel(poliza?.formaPago)
    const recibos = [...(poliza?.recibos || [])].sort((left, right) => Number(left.serie || 0) - Number(right.serie || 0))
    const paid = recibos.filter(isPaidRecibo)
    const pending = recibos.filter(isPendingRecibo)
    const next = nextPendingRecibo(recibos)
    const pagado = sumTotal(paid)
    const porCobrar = sumTotal(pending)
    const { pct, monto: comision } = policyCommission(poliza)
    const conducto = poliza?.conductoDescripcion || (poliza?.conducto ? CONDUCTO_LABELS[poliza.conducto] : null) || labelize(poliza?.conducto)
    const monedaLabel = poliza?.moneda ? [poliza.moneda.abreviacion, poliza.moneda.moneda].filter(Boolean).join(' · ') : currency

    const generales: PolicyFact[] = poliza ? [
        { label: 'Ramo', value: ramoName(poliza.ramo) },
        { label: 'Contratante', value: name, to: cliente?.id ? `/clientes/${cliente.id}` : undefined },
        { label: 'Aseguradora', value: aseguradoraName(poliza) },
        { label: 'Clave de agente', value: poliza.agenteClave?.clave },
        { label: 'No. de póliza', value: poliza.noPoliza },
        { label: 'Plan / paquete', value: poliza.paquete },
        { label: 'Concepto', value: poliza.concepto, wide: true },
        { label: 'Inicio de vigencia', value: formatSpokenDate(poliza.fechaVigenciaDe) },
        { label: 'Fin de vigencia', value: formatSpokenDate(poliza.fechaVigenciaA) },
        { label: 'Forma de pago', value: forma },
        { label: 'Moneda', value: monedaLabel },
        { label: 'Vendedor', value: poliza.vendedor ? personName(poliza.vendedor) : null },
        { label: 'Conducto de cobro', value: conducto },
        { label: 'Fecha de emisión', value: formatSpokenDate(poliza.fechaCreacion) },
        ...(poliza.polizaIDRenovo ? [{ label: 'Renovación', value: 'Ver póliza renovada', to: policyPath(poliza.polizaIDRenovo) }] : []),
        ...(poliza.texto ? [{ label: 'Observaciones', value: plainText(poliza.texto), wide: true }] : []),
    ] : []

    const aseguradoCards: AseguradoCard[] = [
        ...(poliza?.asegurados || []).map((asegurado, index) => {
            const nombre = personName(asegurado)
            return {
                key: `asegurado-${asegurado.id ?? index}`,
                rol: aseguradoRol(asegurado, index),
                nombre: nombre || 'Sin nombre',
                esContratante: Boolean(nombre && name && nombre.toLowerCase() === name.toLowerCase()),
                vehiculo: false,
                facts: [
                    { label: 'Nombres', value: asegurado.nombres },
                    { label: 'Apellidos', value: [asegurado.apPat, asegurado.apMat].filter(Boolean).join(' ') },
                    { label: 'Género', value: GENEROS.find((item) => item.id === asegurado.genero)?.label },
                    { label: 'RFC', value: asegurado.rfc },
                    { label: 'Fecha de nacimiento', value: formatWithAge(asegurado.fecNacimiento), wide: true },
                ],
            }
        }),
        ...(poliza?.vehiculo ? [{
            key: 'vehiculo',
            rol: 'Bien asegurado',
            nombre: vehicleLabel(poliza.vehiculo) || 'Vehículo',
            esContratante: false,
            vehiculo: true,
            facts: [
                { label: 'Marca', value: poliza.vehiculo.marca },
                { label: 'Modelo', value: poliza.vehiculo.modelo ? String(poliza.vehiculo.modelo) : null },
                { label: 'Descripción', value: poliza.vehiculo.descripcion, wide: true },
                { label: 'Placas', value: poliza.vehiculo.placas },
                { label: 'Número de serie', value: poliza.vehiculo.noSerie },
                { label: 'Tipo', value: poliza.vehiculo.tipo },
                { label: 'AMIS', value: poliza.vehiculo.amis },
            ],
        }] : []),
    ]

    const ajuste = Number(poliza?.ajuste || 0)
    const primas: PolicyFact[] = poliza ? [
        { label: 'Prima neta', value: money(poliza.prima, currency) },
        { label: 'Comisión', value: comision != null ? [pct != null ? `${pct}%` : null, money(comision, currency)].filter(Boolean).join(' · ') : null },
        { label: 'Derecho de póliza', value: money(poliza.derecho, currency) },
        { label: 'Financiamiento', value: money(poliza.recargo, currency) },
        ...(ajuste ? [{ label: 'Ajuste', value: money(ajuste, currency) }] : []),
        { label: 'IVA', value: money(poliza.iva, currency) },
        { label: 'Forma de pago', value: forma ? `${forma}${recibos.length ? ` · ${recibos.length} recibo${recibos.length === 1 ? '' : 's'}` : ''}` : null },
        { label: 'Conducto de cobro', value: conducto },
    ] : []

    const desglose: DesgloseRow[] = poliza ? [
        { label: 'Prima neta', value: money(poliza.prima, currency) },
        { label: 'Derecho + financiamiento', value: money(Number(poliza.derecho || 0) + Number(poliza.recargo || 0), currency) },
        ...(ajuste ? [{ label: 'Ajuste', value: money(ajuste, currency) }] : []),
        { label: 'IVA', value: money(poliza.iva, currency) },
        { label: 'Prima total', value: money(poliza.total, currency), total: true },
    ] : []

    const canPay = canAccess(Permisos.POLIZAS_COBRANZA)
    const reciboRows: ReciboRow[] = recibos.map((recibo, index) => ({
        key: String(recibo.id ?? index),
        numero: recibo.totalSerie ? `${recibo.serie ?? index + 1}/${recibo.totalSerie}` : String(recibo.serie ?? index + 1),
        vence: formatSpokenDate(recibo.fecVence) || '—',
        prima: money(recibo.prima, currency),
        derecho: money(recibo.derecho, currency),
        recargo: money(recibo.recargo, currency),
        iva: money(recibo.iva, currency),
        total: money(recibo.total, currency),
        chip: reciboChip(recibo),
        pagable: canPay && !closed && isPendingRecibo(recibo) && recibo.id != null,
        recibo,
    }))

    const notas = bitacora.filter((item) => statusKey(item.tipo) !== 'adjunto')

    const activity: ActivityItem[] = [
        ...notas.map((item, index) => {
            const meta = noteMeta(item.tipo)
            const text = plainText(item.texto)
            return {
                key: `nota-${item.id ?? index}`,
                title: text ? snippet(text) : meta.label,
                date: [meta.label, formatActivityDate(item.fecha)].filter(Boolean).join(' · '),
                tone: meta.filter as ActivityTone,
                time: timeOf(item.fecha),
            }
        }),
        ...paid.filter((item) => item.fecPago).map((item) => ({
            key: `pago-${item.id}`,
            title: `Recibo ${item.serie ?? ''}${item.totalSerie ? `/${item.totalSerie}` : ''} pagado`,
            date: [formatSpokenDate(item.fecPago), item.folioPago].filter(Boolean).join(' · '),
            tone: 'pago' as ActivityTone,
            time: timeOf(item.fecPago),
        })),
        ...(poliza?.fechaCreacion ? [{
            key: 'emision',
            title: 'Póliza registrada en Kartera',
            date: formatSpokenDate(poliza.fechaCreacion) || '',
            tone: 'emision' as ActivityTone,
            time: timeOf(poliza.fechaCreacion),
        }] : []),
    ].sort((left, right) => right.time - left.time).slice(0, ACTIVITY_LIMIT).map(({ time, ...item }) => item)

    const reciboAdjuntos = adjuntos.filter((item) => item.reciboId != null).length
    const adjuntoFilters: Array<{ id: AdjuntoFilter; label: string }> = [
        { id: 'todos', label: `Todos (${adjuntos.length})` },
        { id: 'poliza', label: 'Póliza' },
        { id: 'recibos', label: reciboAdjuntos ? `Recibos (${reciboAdjuntos})` : 'Recibos' },
        { id: 'pdf', label: 'PDF' },
        { id: 'img', label: 'Imágenes' },
        { id: 'xls', label: 'Hojas de cálculo' },
    ]
    const visibleAdjuntos: AdjuntoRow[] = adjuntos.filter((item) => {
        const ext = fileExt(item.archivo)
        if (adjuntoFilter === 'poliza') return item.reciboId == null
        if (adjuntoFilter === 'recibos') return item.reciboId != null
        if (adjuntoFilter === 'pdf') return ext === 'PDF'
        if (adjuntoFilter === 'img') return IMAGE_EXT.includes(ext)
        if (adjuntoFilter === 'xls') return ext === 'XLS' || ext === 'CSV'
        return true
    }).map((adjunto) => ({
        adjunto,
        tag: adjunto.reciboId != null ? reciboTag(recibos, adjunto.reciboId) : 'Póliza',
        fromRecibo: adjunto.reciboId != null,
    }))

    const tabs: PolicyReadTabItem[] = [
        { id: 'general', label: 'Datos generales' },
        { id: 'asegurados', label: poliza?.vehiculo && !poliza.asegurados?.length ? 'Bien asegurado' : 'Asegurados', count: poliza ? aseguradoCards.length : null },
        { id: 'primas', label: 'Primas' },
        { id: 'recibos', label: 'Recibos', count: poliza ? recibos.length : null },
        ...(canBitacora ? [{ id: 'bitacora' as const, label: 'Bitácora', count: bitacoraState === 'ready' ? notas.length : null }] : []),
        ...(canAdjuntos ? [{ id: 'adjuntos' as const, label: 'Adjuntos', count: adjuntosState === 'ready' ? adjuntos.length : null }] : []),
    ]

    const vigencia = poliza ? vigenciaView(poliza.fechaVigenciaDe, poliza.fechaVigenciaA, poliza.status) : null

    return {
        poliza,
        loading,
        error,
        tab,
        tabs,
        toast,
        closing,
        paying,
        name,
        cliente,
        currency,
        status,
        closed,
        subtitle: poliza ? [ramoName(poliza.ramo), aseguradoraName(poliza), poliza.paquete].filter(Boolean).join(' · ') : '',
        vigencia,
        vigenciaText: poliza ? `Vigencia ${formatSpokenDate(poliza.fechaVigenciaDe) || '—'} – ${formatSpokenDate(poliza.fechaVigenciaA) || '—'}` : '',
        restan: poliza ? restanText(poliza.status, poliza.fechaVigenciaA) : null,
        canRenew,
        hasCaratula: Boolean(caratulaPath),
        caratulaHint,
        generales,
        aseguradoCards,
        primas,
        desglose,
        reciboRows,
        recibosSummary: `${paid.length} de ${recibos.length} recibo${recibos.length === 1 ? '' : 's'} pagado${paid.length === 1 ? '' : 's'} · por cobrar ${money(porCobrar, currency)}`,
        totalText: money(poliza?.total, currency),
        recibosText: [recibos.length ? `${recibos.length} recibo${recibos.length === 1 ? '' : 's'}` : null, forma].filter(Boolean).join(' · ') || ' ',
        pagadoText: money(pagado, currency),
        porCobrarText: money(porCobrar, currency),
        proximoText: next ? `${formatSpokenDate(next.fecVence) || '—'} · ${money(next.total, currency)}` : 'Sin pendientes',
        comisionText: comision != null ? `${money(comision, currency)}${pct != null ? ` (${pct}%)` : ''}` : '—',
        activity,
        bitacoraState,
        bitacoraError,
        bitacoraFilter,
        visibleNotes: notas.filter((item) => bitacoraFilter === 'todo' || noteMeta(item.tipo).filter === bitacoraFilter),
        noteType,
        note,
        notePlaceholder: NOTE_TYPES.find((item) => item.id === noteType)?.placeholder,
        savingNote,
        adjuntos,
        adjuntosState,
        adjuntosError,
        adjuntoFilter,
        adjuntoFilters,
        visibleAdjuntos,
        uploading,
        pendingUploads,
        pendingTooBig: pendingUploads.some((item) => item.tooBig),
        confirmDeleteId,
        backPath: POLICIES_PATH,
        setTab,
        setNoteType,
        setNote,
        setBitacoraFilter,
        setAdjuntoFilter,
        setConfirmDeleteId,
        dismissToast: () => setToast(null),
        reload,
        openFile,
        openCaratula,
        renewPolicy,
        editPolicy,
        addNote,
        selectFiles,
        cancelUpload,
        confirmUpload,
        deleteAdjunto,
        payRecibo,
        cancelPay: () => setPaying(null),
        paymentCompleted,
        openClose: () => setClosing(true),
        cancelClose: () => setClosing(false),
        closeCompleted,
    }
}
